/**
 * Модель HUD боя: всё, что рисует {@link HudView}, собранное чистой функцией {@link toHudModel} из снимка боя
 * (`BattleSnapshot`, каждый тик) и подтверждённого состояния игрока (`PlayerState`, может ещё не прийти).
 * Компоненты HUD не читают снимок напрямую — так их можно тестировать и показывать на моках.
 */
import type { BattleSnapshot, HeroId, HqLook, OfferPlace, PlayerState, WavePhase } from "@newfungame/contracts";
import { TICK_RATE } from "@newfungame/contracts";
import { balanceV1, dailyTasksClaimable, seasonStep, seasonUnclaimed } from "@newfungame/engine";
import { permille } from "../kit/format";

/** Панели, которые открываются с боковых иконок HUD. */
export type HudPanel = "tasks" | "streak" | "season" | "chests" | "friends" | "rank";

/** Колбэки HUD → бой и оболочка. Подключает адаптер (./Hud.tsx) или I1. */
export interface HudActions {
  /** Тап по готовому навыку героя → команда `castSkill`. */
  onCastSkill(heroId: HeroId): void;
  onTapParcel(parcelId: number): void;
  /** Переключатель «Автонавыки» → команда `setAutoSkills`. */
  onToggleAuto(enabled: boolean): void;
  /** Переключатель x1/x2. */
  onToggleSpeed(fast: boolean): void;
  /** Кнопка скорости админа: следующий шаг x1 → x2 → x5 → x10. */
  onAdminSpeed(): void;
  /** Кнопка монет админа. */
  onAdminCoins(): void;
  /** Тап по кнопке с замком → окно VIP. */
  onLocked(place: OfferPlace): void;
  /** Боковая иконка → открыть окно/панель. */
  onOpen(panel: HudPanel): void;
}

export interface HudWaveNode {
  wave: number;
  boss: boolean;
  current: boolean;
}

/** Ближайшая цель в полосе волн: герой, который откроется, и босс той же волны. */
export interface HudGoal {
  wave: number;
  boss: boolean;
  heroId: HeroId;
}

/** Откуда HUD знает, когда открываются герои (`balance.heroes`). */
export type HeroUnlock = { id: HeroId; unlockWave: number };

export interface HudSkill {
  heroId: HeroId;
  skillId: string;
  ready: boolean;
  /** Навык сейчас действует. */
  active: boolean;
  /** Осталось перезарядки, промилле от полной (1000 — только что применён, 0 — готов). */
  cooldownPermille: number;
  /** Осталось секунд до готовности (вверх), 0 — готов. */
  secondsLeft: number;
}

export interface HudSideIcon {
  panel: HudPanel;
  /** Красная точка «есть что забрать». */
  badge: boolean;
  /** Подпись на иконке: шаг сезона. */
  label?: string;
}

export interface HudModel {
  coins: number;
  earnedThisWave: number;
  wave: {
    number: number;
    isBoss: boolean;
    phase: WavePhase;
    /** Прогресс волны, промилле. */
    progress: number;
    /** 4 узла полосы волн: текущая, две следующие и ближайший босс (череп). */
    nodes: HudWaveNode[];
    /** Цель над полем; `null` — все герои открыты. */
    goal: HudGoal | null;
  };
  /** Босс на поле — полоса его прочности вместо полосы волн. */
  boss: { bossId: string; hp: number; hpMax: number; permille: number } | null;
  hq: { hp: number; hpMax: number; permille: number; look: HqLook };
  /** Кнопки навыков героев, по слоту платформы. */
  skills: HudSkill[];
  /**
   * Автонавыки: только у VIP, без него кнопка с замком. `hidden` — замок не показываем: игрок ещё не упирался в таймер
   * (ISSUE-136).
   */
  autoSkills: { enabled: boolean; locked: boolean; hidden: boolean };
  /** Скорость боя x2: только у VIP. */
  speed: { fast: boolean; locked: boolean; hidden: boolean };
  /** Кнопки ускорения: только аккаунтам, которым это разрешил сервер. */
  admin: { speed: number } | null;
  side: { left: HudSideIcon[]; right: HudSideIcon[] };
}

/** Часть состояния игрока, нужная HUD. */
export type HudPlayer = Pick<
  PlayerState,
  "bestWave" | "streak" | "tasks" | "season" | "league" | "coins" | "vip" | "heroes"
>;

/**
 * Какие боковые иконки есть в версии и с какой лучшей волны они появляются («кнопки появляются по прогрессу», ISSUE-136):
 * задания и серия — после волны 3, сезон, друзья и рейтинг — после босса 10. `null` — в этой версии иконки нет (v2:
 * сундуки). Включить — поставить волну.
 */
export const SIDE_UNLOCK: Readonly<Record<HudPanel, number | null>> = {
  tasks: 3,
  streak: 3,
  season: 10,
  chests: null,
  friends: 10,
  rank: 10,
};

/**
 * Иконки, которые ждут ещё и первой прокачки: первая покупка по карману только после волны 3, и палец «Прокачай» не
 * должен появляться в одну секунду с новыми иконками.
 */
const AFTER_FIRST_UPGRADE: readonly HudPanel[] = ["tasks", "streak"];

/** Игрок уже что-то прокачал: уровень или звезда героя выше начальных. */
export function upgradedOnce(player: Pick<PlayerState, "heroes"> | null): boolean {
  return player?.heroes.some((h) => h.level > 1 || h.stars > 0) ?? false;
}

const LEFT: readonly HudPanel[] = ["tasks", "streak", "season", "chests"];
const RIGHT: readonly HudPanel[] = ["friends", "rank"];

/** Босс — каждая 5-я волна (как в engine). */
export const BOSS_EVERY = 5;
const isBossWave = (n: number) => n % BOSS_EVERY === 0;

/**
 * Узлы полосы волн: `[n, n+1, n+2, X]`, где X — ближайшая волна босса после n+2, если среди первых трёх босса нет,
 * иначе просто n+3. Так на полосе всегда видно, сколько до босса (макеты 01 и 03).
 */
export function waveNodes(current: number): HudWaveNode[] {
  const first = [current, current + 1, current + 2];
  let last = current + 3;
  if (!first.some(isBossWave)) last = Math.ceil(last / BOSS_EVERY) * BOSS_EVERY;
  return [...first, last].map((wave) => ({ wave, boss: isBossWave(wave), current: wave === current }));
}

/** Ближайший неоткрытый герой: волна `bestWave + 1` уже достигнута, её герой на поле. */
export function nextGoal(unlocks: readonly HeroUnlock[], bestWave: number): HudGoal | null {
  const next = unlocks.filter((h) => h.unlockWave > bestWave + 1).sort((a, b) => a.unlockWave - b.unlockWave)[0];
  return next ? { wave: next.unlockWave, boss: isBossWave(next.unlockWave), heroId: next.id } : null;
}

/** Есть что забрать в окне панели. */
function panelBadge(panel: HudPanel, player: HudPlayer | null): boolean {
  if (!player) return false;
  if (panel === "streak") return !player.streak.claimedToday;
  if (panel === "tasks") return player.tasks ? dailyTasksClaimable(player.tasks).length > 0 : false;
  if (panel === "rank") return (player.league?.unclaimed ?? 0) > 0;
  if (panel === "season" && player.season && balanceV1.season) {
    return seasonUnclaimed(balanceV1, balanceV1.season, player.season, player.bestWave).coins > 0;
  }
  return false;
}

/** Шаг сезона на иконке: видно движение шкалы при каждом заходе. */
function panelLabel(panel: HudPanel, player: HudPlayer | null): string | undefined {
  const def = balanceV1.season;
  if (panel !== "season" || !player?.season || !def) return undefined;
  return `${seasonStep(def, player.season.points)}/${def.steps}`;
}

function sideIcons(panels: readonly HudPanel[], bestWave: number, player: HudPlayer | null): HudSideIcon[] {
  return panels
    .filter((p) => {
      const at = SIDE_UNLOCK[p];
      if (at === null || bestWave < at) return false;
      if (AFTER_FIRST_UPGRADE.includes(p) && !upgradedOnce(player)) return false;
      if (p === "tasks") return Boolean(player?.tasks);
      return p !== "season" || Boolean(player?.season);
    })
    .map((panel) => {
      const label = panelLabel(panel, player);
      return { panel, badge: panelBadge(panel, player), ...(label ? { label } : {}) };
    });
}

/** Собрать модель HUD. `player` — `null`, пока сервер не ответил (тогда прогресс берётся из номера волны). */
export function toHudModel(
  s: BattleSnapshot,
  player: HudPlayer | null = null,
  fast = false,
  speed: boolean = player?.vip.active ?? false,
  unlocks: readonly HeroUnlock[] = [],
  adminSpeed: number | null = null,
  /** Показывать ли закрытые x2 и «Авто» (после первого таймера); открытые видны всегда. */
  vipTeaser = true,
): HudModel {
  const vip = player?.vip.active ?? false;
  const autoOpen = vip || adminSpeed !== null;
  const w = s.wave;
  // дошёл до волны n — значит n − 1 уже пройдена; сервер может знать больше (лучшая волна прошлых забежек)
  const bestWave = Math.max(player?.bestWave ?? 0, w.number - 1);
  const skills = [...s.heroes]
    .sort((a, b) => a.slot - b.slot)
    .map<HudSkill>((h) => {
      const { cooldownLeft, cooldownTotal, activeLeft, skillId } = h.skill;
      return {
        heroId: h.heroId,
        skillId,
        ready: cooldownLeft === 0,
        active: activeLeft > 0,
        cooldownPermille: permille(cooldownLeft, cooldownTotal),
        secondsLeft: Math.ceil(cooldownLeft / TICK_RATE),
      };
    });
  return {
    // источник истины — стор игрока (клейм и покупки между волнами) + монеты текущей волны; снимок боя фиксирует базу на старте волны
    coins: player ? player.coins + s.wallet.earnedThisWave : s.wallet.coins,
    earnedThisWave: s.wallet.earnedThisWave,
    wave: {
      number: w.number,
      isBoss: w.isBoss,
      phase: w.phase,
      progress: w.progress,
      nodes: waveNodes(w.number),
      goal: nextGoal(unlocks, bestWave),
    },
    boss: w.boss
      ? { bossId: w.boss.bossId, hp: w.boss.hp, hpMax: w.boss.hpMax, permille: permille(w.boss.hp, w.boss.hpMax) }
      : null,
    hq: { hp: s.hq.hp, hpMax: s.hq.hpMax, permille: permille(s.hq.hp, s.hq.hpMax), look: s.hq.look },
    skills,
    autoSkills: { enabled: autoOpen && s.autoSkills.enabled, locked: !autoOpen, hidden: !autoOpen && !vipTeaser },
    speed: { fast: speed && fast, locked: !speed, hidden: !speed && !vipTeaser },
    admin: adminSpeed === null ? null : { speed: adminSpeed },
    side: { left: sideIcons(LEFT, bestWave, player), right: sideIcons(RIGHT, bestWave, player) },
  };
}
